'use client';

import { generateBreadcrumbs } from '@/lib/breadcrumbs';
import { IBreadcrumbItem } from '@/interfaces';
import { Language } from '@/lib/translations';
import { usePathname } from 'next/navigation';
import Link from 'next/link';

interface BreadcrumbProps {
  language: Language;
  className?: string;
  // Explicit breadcrumb items; when omitted they are derived from the pathname
  items?: IBreadcrumbItem[];
}

export default function Breadcrumb({
  language,
  className = '',
  items,
}: BreadcrumbProps) {
  // Get pathname from URL
  const pathname = usePathname();

  // Use explicit items when provided, otherwise generate from pathname
  const breadcrumbs = items ?? generateBreadcrumbs(pathname, language);

  // Don't render if no breadcrumbs (home page)
  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <nav
      className={`flex text-sm ${className}`}
      style={{ color: 'var(--color-foreground-secondary)' }}
    >
      {breadcrumbs.map((item, index) => (
        <div key={index} className="flex items-center">
          {index > 0 && <span className="mx-2">/</span>}
          {item.href ? (
            <Link
              href={item.href}
              className="hover:text-[#3880ff] dark:hover:text-blue-400 transition-colors flex items-center gap-1"
            >
              {index === 0 && (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
              )}
              {item.name}
            </Link>
          ) : (
            <span
              className="font-medium"
              style={{ color: 'var(--color-foreground)' }}
            >
              {item.name}
            </span>
          )}
        </div>
      ))}
    </nav>
  );
}
